import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  FormControl,
  Grid,
  InputLabel,
  List,
  ListItem,
  MenuItem,
  Select,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import { compact, isEmpty, isNil, map, reduce } from 'lodash';

import lineItemStatusEnum from 'enums/lineItem.enum';
import orderEnum from 'enums/order.enum';
import LoadingModule from 'common/components/LoadingModule';
import { getPatientOrderHistory } from 'store/thunks/patientThunks';
import { ORDER_HISTORY_STATUSES } from 'common/constants/orderStatusGroups';
import { PrintLabelButton, PrintLabelProvider } from 'common/hooks/usePrintLabelContext';
import useRoles from 'common/hooks/useRoles';
import useTabContext from 'common/hooks/useTabContext';
import NothingFound from 'common/components/NothingFound';

import PatientOrdersAccordion from '../EligibilityActionItems/PatientOrdersAccordion';

const OrderHistoryTab = () => {
  const hasAccess = useRoles();
  const dispatch = useDispatch();
  const { mpi } = useParams();
  const orders = useSelector(({ patient }) => patient[mpi]?.orderHistory);
  const isActiveTab = useTabContext()?.tab === 'OrderHistoryTab';
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isActiveTab) {
      const statusParam = statusFilter === 'ALL' ? ORDER_HISTORY_STATUSES : [statusFilter];
      setIsLoading(true);
      dispatch(
        getPatientOrderHistory({
          mpi,
          status: statusParam,
          includeAllFacilities: true,
        })
      ).finally(() => setIsLoading(false));
    }
  }, [dispatch, mpi, isActiveTab, statusFilter]);

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          mb: 2,
          width: '100%',
        }}
      >
        <FormControl size='small' sx={{ minWidth: 180 }}>
          <InputLabel id='order-history-status-filter-label'>Status</InputLabel>
          <Select
            labelId='order-history-status-filter-label'
            label='Status'
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value='ALL'>All</MenuItem>
            <MenuItem value='SHIPPED'>Shipped</MenuItem>
            <MenuItem value='CANCELED'>Cancelled</MenuItem>
            <MenuItem value='EXPIRED'>Expired</MenuItem>
            <MenuItem value='TRANSFERRED'>Transferred</MenuItem>
            <MenuItem value='FILL_LATER'>Fill Later</MenuItem>
          </Select>
        </FormControl>
      </Box>
      {isNil(orders) || isLoading ? (
        <LoadingModule />
      ) : (
        <List>
          <PrintLabelProvider
            initialValue={reduce(
              orders,
              (result, order) => [
                ...result,
                ...(!order.isProcessedExternal
                  ? [
                      ...compact(
                        map(order.lineItems, (lineItem) =>
                          !lineItem?.isPrinted &&
                          lineItem.lineItemStatus !== lineItemStatusEnum.RE_PROCESS &&
                          lineItem.lineItemStatus !== lineItemStatusEnum.CANCELED &&
                          lineItem.processingState !== lineItemStatusEnum.CANCELED &&
                          order.status !== orderEnum.CANCELED &&
                          order.status !== orderEnum.EXPIRED &&
                          order.status !== orderEnum.FILL_LATER &&
                          lineItem?.isLabelCreated
                            ? lineItem?.lineItemId
                            : null
                        )
                      ),
                    ]
                  : []),
              ],
              []
            )}
          >
            {!isEmpty(orders) && hasAccess.generateBulkLabels && (
              <Grid sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <PrintLabelButton />
              </Grid>
            )}

            <List>
              {isEmpty(orders) ? (
                <Grid item>
                  <NothingFound />
                </Grid>
              ) : (
                <>
                  {map(orders, (order) => (
                    <ListItem key={order.orderId}>
                      <PatientOrdersAccordion order={order} mpi={mpi} />
                    </ListItem>
                  ))}
                </>
              )}
            </List>
          </PrintLabelProvider>
        </List>
      )}
    </>
  );
};

export default OrderHistoryTab;
